import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useActiveLoyaltyConfig } from './useActiveLoyaltyConfig'
import { useSaveLoyaltyConfig } from './useSaveLoyaltyConfig'
import type { CardIconKey } from '@chillout/ui'
import { loyaltyConfigSchema } from './loyaltySchemas'
import type { RewardItem } from '../shared/rewardItems'

export type { RewardItem }

export interface Milestone {
  stampPosition: number
  rewardItemId: string
  cardIcon: CardIconKey
}

export interface LoyaltyConfig {
  earningMethod: 'spend_based' | 'per_meal'
  amountPerStamp: number
  stampTarget: number
  completionRewardItemId: string
  completionCardIcon: CardIconKey
  milestones: Milestone[]
}

const EMPTY_CONFIG: LoyaltyConfig = {
  earningMethod: 'spend_based',
  amountPerStamp: 100,
  stampTarget: 12,
  completionRewardItemId: '',
  completionCardIcon: 'Gift',
  milestones: [],
}

export function useLoyaltyConfig() {
  const { t } = useTranslation()
  const configQuery = useActiveLoyaltyConfig()

  const form = useForm<LoyaltyConfig>({
    resolver: zodResolver(loyaltyConfigSchema(t)),
    defaultValues: EMPTY_CONFIG,
    mode: 'onChange',
  })

  useEffect(() => {
    if (configQuery.data) form.reset(configQuery.data)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configQuery.data])

  const config = form.watch()
  const hasChanges = form.formState.isDirty
  const assignedRewardItemIds = new Set(
    [config.completionRewardItemId, ...config.milestones.map((m) => m.rewardItemId)].filter(Boolean),
  )

  const [pendingConfig, setPendingConfig] = useState<LoyaltyConfig | null>(null)
  const saveMutation = useSaveLoyaltyConfig()

  function reset() {
    if (configQuery.data) form.reset(configQuery.data)
  }

  const requestSave = form.handleSubmit((validated) => {
    saveMutation.reset()
    setPendingConfig(validated)
  })

  function confirmSave() {
    if (!pendingConfig) return
    saveMutation.mutate(pendingConfig, {
      onSuccess: () => {
        form.reset(pendingConfig)
        setPendingConfig(null)
      },
    })
  }

  function cancelSave() {
    setPendingConfig(null)
    saveMutation.reset()
  }

  return {
    control: form.control,
    config,
    serverConfig: configQuery.data ?? EMPTY_CONFIG,
    pendingConfig,
    assignedRewardItemIds,
    hasChanges,
    isSaving: saveMutation.isPending,
    saveError: saveMutation.isError,
    isLoading: configQuery.isLoading,
    isError: configQuery.isError,
    onRetry: () => void configQuery.refetch(),
    reset,
    requestSave,
    confirmSave,
    cancelSave,
  }
}